import { ResponsiveImage } from "@components/atoms/ResponsiveImage"
import { Button } from "@components/ui/Button"
import { tv, VariantProps } from "@utils/tw"
import { Link } from "eddev/routing"
import { ReactNode } from "react"
import { BreadcrumbsFragment, ButtonFragment, ResponsiveImageFragment } from "@generated-types"
import { Breadcrumbs } from "./Breadcrumbs"
import { IconName } from "@components/ui/Icon"
import { useButtonProps } from "@hooks/useButtonProps"

type Props = {
  title: ReactNode
  breadcrumbs?: (BreadcrumbsFragment | null)[]
  description?: ReactNode
  hasDescription?: boolean
  mixpanelArea?: string
  image?: ResponsiveImageFragment
  links?: Partial<ButtonFragment>[]
} & Pick<VariantProps<typeof createStyles>, "theme">

export function PageHeader(props: Props) {
  const hasImage = !!props.image
  const hasLinks = !!props.links?.length
  const hasDescription = !!props.description
  const hasDivider = !hasImage && (props.theme === "white" || !props.theme)
  const styles = createStyles({
    theme: props.theme || "grey",
    hasImage,
    marginBottom: !hasDivider,
  })

  return (
    <>
      {/* Breadcrumbs, if there are any */}
      <div className={props.theme === "black" ? "theme-dark bg-bg text-fg" : ""}>
        {!!props.breadcrumbs?.length && <Breadcrumbs breadcrumbs={props.breadcrumbs} />}
      </div>
      <div className={styles.base()}>
        <div className={styles.layout()}>
          <div className={styles.info()}>
            <h1 className={"type-title-xl pb-8 text-pretty"}>{props.title}</h1>
            <div className={"type-body-l max-w-[30em] text-pretty"}>{props.description}</div>
          </div>
          <div className={styles.imageContainer()}>
            {props.image && (
              <div className="flex h-full self-stretch relative aspect-video md:-mr-grid-margin">
                <ResponsiveImage {...props.image} max />
              </div>
            )}
            {hasLinks && (
              <div className={styles.linkCluster()}>
                {props.links?.map((link, index) => <ButtonLink {...link} key={index} />)}
              </div>
            )}
          </div>
        </div>
        <div className={styles.divider()} />
      </div>
    </>
  )
}

function ButtonLink(props: Partial<ButtonFragment>) {
  const buttonProps = useButtonProps(props as ButtonFragment, {
    defaultVariant: "ghost",
    primaryVariant: "primary",
  })

  return (
    <Button {...buttonProps} className="w-full justify-between" icon={undefined} rightIcon={buttonProps.icon}></Button>
  )
}

const createStyles = tv({
  base: "w-full bg-bg text-fg md:overflow-hidden",
  slots: {
    layout: "flex flex-col md:flex md:flex-row md:gap-grid-gutter md:w-grid mx-auto",
    info: "w-full px-grid-margin md:px-0 sm:w-cols-6 py-6",
    imageContainer: "sm:w-cols-6 relative",
    linkCluster: "py-5 px-2 sm:p-5 stack-y-1 w-full sm:w-auto",
    divider: "w-grid mx-auto border-t border-t-bg-mid mb-6",
  },
  variants: {
    theme: {
      white: { base: "theme-light" },
      grey: { base: "theme-light bg-bg-low", divider: "hidden" },
      black: { base: "theme-dark bg-bg", divider: "hidden" },
    },
    hasImage: {
      true: {
        info: "flex flex-col justify-between sm:w-full md:w-cols-5",
        imageContainer: "w-full sm:w-full md:w-cols-7",
        linkCluster: "sm:absolute left-0 bottom-0 sm:bg-bg md:min-w-[50%]",
        divider: "sm:hidden",
      },
      false: {
        imageContainer: "flex sm:justify-end py-6",
        layout: "sm:flex-row",
        linkCluster: "pt-0 sm:items-end",
      },
    },
    marginBottom: {
      true: "mb-8",
    },
  },
})
